/**
 * Configure online-3d-viewer to load its WASM dependencies
 * (occt-import-js, rhino3dm, draco3d) from a local, same-origin path
 * instead of the default ``cdn.jsdelivr.net`` URL.
 *
 * Why this exists
 * ---------------
 * online-3d-viewer's STEP loader is the JS shim of ``occt-import-js``; both
 * the ``.js`` file and its companion ``.wasm`` are fetched from
 * ``https://cdn.jsdelivr.net/npm/occt-import-js@<v>/dist/`` at first use. On a
 * locked-down corporate / shop-floor network (or under a strict CSP that
 * forgets to whitelist jsdelivr) the load silently fails and the viewer reports
 * a misleading "The model doesn't contain any meshes." error.
 *
 * Hosting the dist files alongside the rest of the app's static assets
 * eliminates the third-party dependency at render time and gives the same
 * origin a working STEP pipeline whenever the page itself loads.
 *
 * Where the files live
 * --------------------
 * The dist files are vendored at
 * ``src/backend/InvenTree/web/static/3d-libs/``, which Django serves at
 * ``/static/3d-libs/...`` in dev (via ``STATICFILES_DIRS``) and prod (via the
 * collected static root). ``SetExternalLibLocation`` takes a directory URL with
 * a trailing slash; the library appends ``occt-import-js.js`` (and friends)
 * itself, and the ``.js`` file then loads its sibling ``.wasm``.
 *
 * Idempotent — safe to call from every entry point that uses the viewer.
 */

let configured = false;

export function configureOvExternalLibs(OV: any): void {
  if (configured) return;
  configured = true;

  // Trailing slash matters — OV appends the filename verbatim.
  const localPath = '/static/3d-libs/';

  // Older builds of the library exposed only ``SetExternalLibLocation``;
  // newer ones add ``LoadExternalLibraryFromUrl``. We only need the location
  // setter, but guard the call so a future API removal doesn't crash the
  // viewer — falling back to jsdelivr still works on networks that allow it.
  if (typeof OV?.SetExternalLibLocation === 'function') {
    OV.SetExternalLibLocation(localPath);
  }
}

/**
 * Brighten the default lighting of an online-3d-viewer instance.
 *
 * The library's default (ambient and directional light at 0x888888) renders
 * coloured models rather dark. The light objects are not part of the public
 * API, so this is best-effort: if the internals change, the viewer simply
 * keeps its default look.
 */
export function brightenOvLighting(embedded: any): void {
  try {
    const shading = embedded?.GetViewer?.()?.shadingModel;
    if (!shading?.ambientLight || !shading?.directionalLight) return;
    shading.ambientLight.color.set(0xe0e0e0);
    shading.directionalLight.color.set(0xb4b4b4);
    embedded.GetViewer().Render();
  } catch {
    // keep the default lighting
  }
}
